/**
 * Grouping for the filter dropdowns.
 *
 * Every group here is DERIVED from data the application already holds — the
 * feature key's own namespace, and the voice-provider registry. Nothing is
 * invented, no option is renamed, and no option is hidden: grouping only
 * changes how an option is found, never which options exist or what selecting
 * one does.
 */
/**
 * Feature keys are namespaced (`report.final`, `turn_detection.candidate`), and
 * that namespace IS the product area. Grouping on it therefore reflects a real
 * relationship rather than an editorial one, and a feature added later lands in
 * the right group with no change here.
 */
const FEATURE_AREAS = [
    { match: (k) => k.startsWith('brain.'), label: 'Interview conversation' },
    { match: (k) => k.startsWith('turn_detection.'), label: 'Turn detection' },
    { match: (k) => k === 'stt' || k === 'tts' || k.startsWith('voice.'), label: 'Voice' },
    { match: (k) => k.startsWith('interview.'), label: 'Interview setup' },
    { match: (k) => k.startsWith('jd.'), label: 'Job description' },
    { match: (k) => k.startsWith('resume.'), label: 'Resume' },
    { match: (k) => k.startsWith('report.'), label: 'Reports' },
    { match: (k) => k.startsWith('code.'), label: 'Coding' },
    { match: (k) => k.startsWith('proctoring.'), label: 'Proctoring' },
    { match: (k) => k.startsWith('transcript.'), label: 'Transcript' },
];
const AREA_ORDER = [
    'Interview conversation',
    'Voice',
    'Turn detection',
    'Interview setup',
    'Job description',
    'Reports',
    'Coding',
    'Proctoring',
    'Transcript',
    'Resume',
    'Other',
];
export const featureGroups = (meta) => {
    var _a;
    const byArea = new Map();
    ((_a = meta === null || meta === void 0 ? void 0 : meta.features) !== null && _a !== void 0 ? _a : []).forEach((f) => {
        var _a, _b;
        const area = (_b = (_a = FEATURE_AREAS.find((a) => a.match(f.key))) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : 'Other';
        if (!byArea.has(area))
            byArea.set(area, []);
        // The technical key stays visible as the secondary line — an administrator
        // reading logs needs it, it just should not outrank the name.
        byArea.get(area).push({ value: f.key, label: f.label || f.key, sub: f.key });
    });
    const groups = [
        { label: null, options: [{ value: '', label: 'All features' }] },
    ];
    AREA_ORDER.forEach((area) => {
        const options = byArea.get(area);
        if (options === null || options === void 0 ? void 0 : options.length) {
            groups.push({ label: area, options: options.sort((a, b) => a.label.localeCompare(b.label)) });
        }
    });
    return groups;
};
/**
 * A provider is a VOICE provider when it appears in the voice-provider
 * registry — the same list the scheduling flow picks from. Anything else is an
 * AI provider. Derived, so registering a new voice provider re-groups it here
 * with no code change.
 */
export const providerGroups = (providers, voiceProviderIds) => {
    const voice = new Set(voiceProviderIds);
    const ai = [];
    const speech = [];
    (providers !== null && providers !== void 0 ? providers : []).forEach((p) => {
        (voice.has(p) ? speech : ai).push({ value: p, label: p });
    });
    const groups = [
        { label: null, options: [{ value: '', label: 'All providers' }] },
    ];
    if (ai.length)
        groups.push({ label: 'AI providers', options: ai });
    if (speech.length)
        groups.push({ label: 'Voice providers', options: speech });
    return groups;
};
export const MODE_LABELS = {
    realtime: 'Realtime',
    cost_optimized: 'Cost-optimized',
    pre_interview: 'Pre-interview',
    unknown: 'Unknown',
};
export const modeGroups = (meta) => {
    var _a;
    return [
        { label: null, options: [{ value: '', label: 'All modes' }] },
        {
            label: 'Interview modes',
            options: ((_a = meta === null || meta === void 0 ? void 0 : meta.modes) !== null && _a !== void 0 ? _a : []).map((m) => {
                var _a;
                return ({
                    value: m,
                    label: (_a = MODE_LABELS[m]) !== null && _a !== void 0 ? _a : m.replace(/_/g, ' '),
                    sub: m,
                });
            }),
        },
    ];
};
/** The label a chip or a "Showing:" line uses for a selected value. */
export const labelFor = (groups, value) => { var _a, _b; return (_b = (_a = groups.flatMap((g) => g.options).find((o) => o.value === value)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : value; };
